/**
 * React Query hook for the public index page.
 * No access key required — the index only exposes table metadata.
 */

import { useQuery } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export function usePublicIndex() {
  const path = `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}/`;

  return useQuery({
    queryKey: ['public-index'],
    queryFn: async () => {
      const resp = await apiHelper.publicApiCall(path, { method: 'GET', cache: 'no-cache' });
      if (resp.status === 404) {
        const err = new Error('Index page not available');
        err.status = 404;
        throw err;
      }
      if (!resp.ok) {
        const err = new Error('Failed to load index');
        err.status = resp.status;
        throw err;
      }
      return resp.json();
    },
    // NOT ZERO, AND THIS IS THE REASON.
    //
    // Two components ask for the index on the same screen: Home renders
    // DynamicHomePage, and PublicPageView reads it for header/footer fallback.
    // They share one query key, so react-query should serve the second from
    // cache — but at staleTime 0 the answer is stale the instant it lands, and
    // the second component's mount triggers refetchOnMount. The home page
    // therefore fetched /arcee/public/<org>/<app>/ twice, every time.
    //
    // Thirty seconds is long enough to cover mounting a page, and short enough
    // that navigating back to a page later still revalidates. `cache:
    // 'no-cache'` above is untouched, so when a refetch does happen it still
    // bypasses the browser's HTTP cache and gets fresh data — and a hard reload
    // rebuilds the QueryClient, so a published page-config change shows up as
    // immediately as it always did.
    staleTime: envConfig.staleTimeMs,
    retry: false,
  });
}
